import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@interstate/components/Button';
import { ArrowUturnLeftIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Tooltip } from '@interstate/components/Tooltip';
import { transformIncentiveAmount } from '@makemydeal/dr-dash-store/utils/manualIncentivesUtils';

import { ContainerGrid, ContainerGridItem, dealLockHooks } from '@makemydeal/dr-dash-components';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';

import { RebatesTableInput } from './RebatesTableInput';
import { StyledTrashIcon } from './Rebates.style';
import type { RebatesTableRowProps } from '../../types';
import { INCENTIVE_FIELD_NAMES } from '../../constants';
import { isIncentiveFromProgram } from '../../utils/rebatesUtils';
import { getIncentiveDisplayType } from '../IncentivesCard/utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const RebatesTableRow: FC<RebatesTableRowProps> = ({
    incentive,
    term,
    codeCellErrorIndexes,
    incentiveIndex,
    isUniqueCode,
    isEditing,
    handleIncentiveDeleteOrUndo,
    handleInputChange,
    onFieldValidationChange,
    showCashProgramLabel = false
}) => {
    const theme = useInterstateTheme();
    const [hasDuplicateCode, setHasDuplicateCode] = useState(false);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);

    const columnMappings = useMemo(
        () =>
            ({
                'Program #': {
                    fieldName: INCENTIVE_FIELD_NAMES.PROGRAM,
                    fieldValue: showCashProgramLabel ? 'CASH' : incentive.program,
                    disabled: showCashProgramLabel,
                    required: false,
                    mobileFieldSize: 4,
                    desktopFieldSize: 2
                },
                'Incentive Name': {
                    fieldName: INCENTIVE_FIELD_NAMES.NAME,
                    fieldValue: incentive.name,
                    required: !isOptionalDeskingMode,
                    displayAsRequired: true,
                    mobileFieldSize: 8,
                    desktopFieldSize: 3
                },
                Code: {
                    fieldName: INCENTIVE_FIELD_NAMES.CODE,
                    fieldValue: incentive.code,
                    required: false,
                    mobileFieldSize: 4,
                    desktopFieldSize: 2
                },
                Type: {
                    fieldName: INCENTIVE_FIELD_NAMES.TYPE,
                    fieldValue: incentive.type,
                    required: !isOptionalDeskingMode,
                    mobileFieldSize: 4,
                    desktopFieldSize: 3
                },
                Amount: {
                    fieldName: INCENTIVE_FIELD_NAMES.AMOUNT,
                    fieldValue: transformIncentiveAmount(term, incentive),
                    required: true,
                    mobileFieldSize: 4,
                    desktopFieldSize: 2
                }
            } as const),
        [incentive, term, isOptionalDeskingMode, showCashProgramLabel]
    );
    const isProgramIncentivesRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const displayType = getIncentiveDisplayType(incentive, isProgramIncentivesRestorationEnabled);

    useEffect(() => {
        if (!incentive.deleted && (isEditing || codeCellErrorIndexes.has(incentiveIndex))) {
            if (!isUniqueCode) {
                codeCellErrorIndexes.add(incentiveIndex);
                setHasDuplicateCode(true);
            } else {
                codeCellErrorIndexes.delete(incentiveIndex);
                setHasDuplicateCode(false);
            }
        }
    }, [incentive, codeCellErrorIndexes, isEditing, isUniqueCode, incentiveIndex]);

    const isFromProgramIncentives = isIncentiveFromProgram(incentive);
    const isRestoreBlocked = incentive.deleted && incentive.code != null && !isUniqueCode;

    const deleteRevertButton = useMemo(() => {
        if (incentive.deleted) {
            const isRestoreDisabled = isRestoreBlocked || isLocked;
            const tooltipContent = isRestoreBlocked
                ? 'Cannot restore: an incentive with this code already exists.'
                : 'Item was deleted. You can still undo this before updating the Offer.';

            return (
                <Button
                    buttonStyle={'secondary'}
                    icon={
                        <Tooltip position="bottom" toolTipContent={tooltipContent} size="large">
                            <ArrowUturnLeftIcon color={isRestoreDisabled ? undefined : '#005BA8'} />
                        </Tooltip>
                    }
                    size="sm"
                    disabled={isRestoreDisabled}
                />
            );
        }

        return (
            <Button
                buttonStyle={'tertiary'}
                icon={
                    <StyledTrashIcon
                        theme={theme}
                        data-testid={`manual-rebate-delete-${incentiveIndex}`}
                        className={`${!isLocked && 'active'}`}
                    />
                }
                size="sm"
                disabled={isLocked}
            />
        );
    }, [incentive.deleted, incentiveIndex, isRestoreBlocked, isLocked]);

    const getDeleteRevertButton = useCallback(() => {
        const isClickBlocked = incentive.deleted && (isRestoreBlocked || isLocked);

        return (
            <ContainerGridItem
                justifyContent="start"
                xs="min-content"
                data-testid={`manual-rebate-trash-icon-${incentiveIndex}`}
                onClick={() => !isClickBlocked && handleIncentiveDeleteOrUndo(incentiveIndex)}
                style={{ marginBottom: '4px' }}
            >
                {deleteRevertButton}
            </ContainerGridItem>
        );
    }, [incentive.deleted, incentiveIndex, isRestoreBlocked, isLocked, handleIncentiveDeleteOrUndo, deleteRevertButton]);

    return (
        <ContainerGrid data-testid={`incentive-row-${incentiveIndex}`} style={{ paddingTop: '1.125rem', alignItems: 'center' }}>
            <ContainerGridItem xs="full">
                <ContainerGrid $alignItems="center" style={{ paddingLeft: '0.5rem' }}>
                    {Object.entries(columnMappings).map(([incentiveLabel, inputProps], columnIndex) => (
                        <ContainerGridItem
                            xs={inputProps.mobileFieldSize}
                            md={inputProps.desktopFieldSize}
                            key={`${incentiveIndex}-${columnIndex}`}
                        >
                            <RebatesTableInput
                                hasCodeDuplicate={hasDuplicateCode}
                                deleted={incentive.deleted}
                                manual={incentive.manual}
                                isFromProgramIncentives={isFromProgramIncentives}
                                isRestorationEnabled={isProgramIncentivesRestorationEnabled}
                                displayType={displayType}
                                incentiveLabel={incentiveLabel}
                                fieldValue={inputProps.fieldValue}
                                fieldName={inputProps.fieldName}
                                required={inputProps.required}
                                displayAsRequired={'displayAsRequired' in inputProps ? inputProps.displayAsRequired : undefined}
                                disabled={'disabled' in inputProps ? inputProps.disabled : undefined}
                                incentiveIndex={incentiveIndex}
                                handleInputChange={handleInputChange}
                                onValidationChange={onFieldValidationChange}
                            />
                        </ContainerGridItem>
                    ))}
                </ContainerGrid>
            </ContainerGridItem>
            {getDeleteRevertButton()}
        </ContainerGrid>
    );
};
